import React, { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import MobileStepper from '@mui/material/MobileStepper';
import Hidden from '@mui/material/Hidden';
import Typography from '@mui/material/Typography';

import BasePage from '@ts-core/pages/BasePage';
import TSTimer from '@ts-core/components/TSTimer/TSTimer';
import Button from '@ts-core/components/Button';

import ExercisePreviewHandler from '@ts-core/modules/exercises/components/ExercisePreviewHandler';
import ExerciseHandler from 'modules/exercises/components/ExerciseHandler';

import { usePracticeSessionActions } from 'modules/practice/hooks';

const DoPracticeSessionPage = () => {
  const history = useHistory();

  const { courseId, practiceSessionId } = useParams();

  const {
    onSelectQuestionOption,
    onContinue,
    selectedOptions,
    practiceSession,
    currentExercise,
    isPracticeSessionFinished,
    isCurrentExerciseFinished,
    submitting,
  } = usePracticeSessionActions({
    courseId,
    practiceSessionId,
  });

  const { currentExerciseIndex, exercisesCount, name } = practiceSession || {};

  const handleContinueAction = useCallback(() => {
    history.replace('results');
  }, [history]);

  function renderTimer() {
    const { timeLimit, startedAt } = currentExercise;
    if (!timeLimit || !startedAt) return null;
    return <TSTimer timeLimit={timeLimit} startedAt={startedAt} />;
  }

  function renderContinueButton() {
    if (isPracticeSessionFinished) {
      return (
        <Button variant="contained" color="primary" onClick={handleContinueAction} loading={submitting}>
          <FormattedMessage id="toResults" />
        </Button>
      );
    }

    return (
      <Button variant="contained" color="primary" loading={submitting} onClick={onContinue}>
        <FormattedMessage id={isCurrentExerciseFinished ? 'practiceSession.nextExercise' : 'practiceSession.sendExerciseAnswers'} />
      </Button>
    );
  }

  if (isEmpty(currentExercise))
    return (
      <Box textAlign="center" width="100%">
        <CircularProgress />
      </Box>
    );

  const steps = Array(exercisesCount).fill(0);
  const activeStep = currentExerciseIndex - 1;

  return (
    <BasePage
      sx={(theme) => ({
        width: '75%',
        margin: '0 auto',
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        [theme.breakpoints.down('lg')]: {
          width: '90%',
        },
        [theme.breakpoints.down('md')]: {
          width: '100%',
          padding: `${theme.spacing()} ${theme.spacing()}`,
        },
      })}
    >
      {renderTimer()}
      <Typography variant="h5">{name}</Typography>
      <Box
        sx={(theme) => ({
          marginTop: theme.spacing(4),
          marginBottom: 36,
          [theme.breakpoints.up('sm')]: {
            marginBottom: 0,
          },
        })}
      >
        <Box sx={{ my: 1 }}>
          <Hidden smDown>
            {steps.length > 1 && (
              <Stepper activeStep={activeStep}>
                {steps.map((step, index) => (
                  <Step key={index} completed={index < activeStep}>
                    <StepLabel />
                  </Step>
                ))}
              </Stepper>
            )}
          </Hidden>
          <Hidden smUp>
            <MobileStepper variant="dots" position="bottom" activeStep={activeStep} steps={exercisesCount} nextButton={renderContinueButton()} />
          </Hidden>
        </Box>
        {isCurrentExerciseFinished ? (
          <ExercisePreviewHandler exercise={currentExercise} />
        ) : (
          <ExerciseHandler
            exercise={currentExercise}
            detailsMode={isCurrentExerciseFinished}
            selectedOptions={selectedOptions}
            onSelectOption={onSelectQuestionOption}
          />
        )}
        <Hidden smDown>
          <Box display="flex" justifyContent="flex-end">
            {renderContinueButton()}
          </Box>
        </Hidden>
      </Box>
    </BasePage>
  );
};

export default DoPracticeSessionPage;
